<template>
  <div class="rsm-feed-wrap">
    <div class="rsm-feed" ref="feedEl">
      <div v-show="activeVideoId" class="rsm-player-layer">
        <div id="rsm-yt-mount" class="rsm-player-layer__mount"></div>
      </div>
      <feed-post
        v-for="post in posts"
        :key="post.id"
        :post="post"
        :is-debug="isDebug"
        :paused="paused"
        @post-seen="onPostSeen"
        @harmful-time-update="onHarmfulTimeUpdate"
        @interaction="onInteraction"
        @video-activated="onVideoActivated"
        @video-event="onVideoEvent"
        @toggle-mute="toggleMute"
        @toggle-pause="togglePause"
      />
    </div>
    <!-- Countdown badge only. The scroll lock is enforced by overflow-y:hidden on the feed
         (see onVideoActivated), not a covering overlay, so the action buttons stay tappable
         during the 3s lock. -->
    <div v-if="lockActive && videoLock === 'countdown'" class="rsm-scroll-lock-badge">
      {{ lockCountdown }}s
    </div>
  </div>
</template>

<script>
import FeedPost from './FeedPost.vue';
import allPosts from '../../data/posts.js';
import {loadYouTubeIframeAPI} from "../../utils/youtube";
import store from "../../store";

const INFINITE_SCROLL_BUFFER_PX  =    500;  // load more when this close to end

export default {
  components: { FeedPost },
  props: {
    condition: { type: String, default: 'control' },
    feedType:  { type: String, default: 'general' },
    shuffle:   { type: Boolean, default: true },
    isDebug:   { type: Boolean, default: false },
    paused:    { type: Boolean, default: false },
    videoLock: { type: String, default: 'countdown' }, // 'countdown' | 'silent' | 'none'
    harmfulThresholdMs: { type: Number, default: 240000 }, // Phase 1: 4 min of harmful content (testing override via ?thresholdSec=)
  },
  data() {
    return {
      posts: [],
      seenPostIds: new Set(),
      harmfulCount: 0,
      harmfulTimeMs: 0,
      chatbotEmitted: false,
      lockActive: false,
      lockCountdown: 3,
      activeVideoId: '',
      _playerPromise: undefined,
      store
    };
  },
  mounted() {
    const filtered = allPosts.filter(p =>
      p.genderFeed === 'both' || p.genderFeed === this.feedType || this.feedType === 'general'
    );
    if (this.shuffle) {
      for (let i = filtered.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [filtered[i], filtered[j]] = [filtered[j], filtered[i]];
      }
    }
    this._sourcePosts = filtered.slice();
    this.posts = filtered;
    this._currentIndex = 0
    this.post = this._sourcePosts[this._currentIndex];

    this._lastScrollTop = 0;
    this.$refs.feedEl.addEventListener('scroll', this._onScroll);
    // Space/Enter are blocked so they can't bubble up and activate the underlying survey's
    // Next button while the feed has focus. But don't block them while the participant is
    // actually typing in a text field (e.g. the "Signaler" technical-issue textarea), or
    // spaces and line breaks stop working there.
    this._keyHandler = (e) => {
      if (e.code !== 'Space' && e.code !== 'Enter') return;
      const tag = e.target && e.target.tagName;
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target && e.target.isContentEditable);
      if (isEditable) return;
      e.preventDefault();
    };
    window.addEventListener('keydown', this._keyHandler);
    this.onVideoActivated(this.post);
  },
  beforeDestroy() {
    this.$refs.feedEl.removeEventListener('scroll', this._onScroll);
    window.removeEventListener('keydown', this._keyHandler);
    clearTimeout(this._scrollTimer);
    clearTimeout(this._scrollLockTimer);
    clearTimeout(this._scrollLockDelay);
    clearInterval(this._countdownInterval);
    if (this._pendingLockHandler) {
      this.$refs.feedEl.removeEventListener('scrollend', this._pendingLockHandler);
    }
  },
  watch: {
    // Mute+pause the shared player while an overlay (chatbot / inoculation) is open, and
    // unmute+resume when it closes. See _teardownPlayer for why this doesn't destroy/recreate
    // the iframe (that approach caused a production incident on unreliable connections).
    paused(isPaused) {
      if (isPaused) {
        this._teardownPlayer();
      } else {
        this._restorePlayer();
      }
    },
  },
  methods: {
    onPostSeen(post) {
      if (this.seenPostIds.has(post.id)) return;
      this.seenPostIds.add(post.id);
      this.$emit('post-viewed', post);

      if (post.harmful) {
        this.harmfulCount++;
        this.$emit('harmful-time-updated', { timeMs: this.harmfulTimeMs, count: this.harmfulCount });
      }
    },
    onHarmfulTimeUpdate(elapsed) {
      if (this.chatbotEmitted) return;
      this.harmfulTimeMs += elapsed;
      this.$emit('harmful-time-updated', { timeMs: this.harmfulTimeMs, count: this.harmfulCount });

      if (this.condition === 'T3' && this.harmfulTimeMs >= this.harmfulThresholdMs) {
        this.chatbotEmitted = true;
        this.$emit('trigger-chatbot');
      }
    },
    onInteraction(event) {
      this.$emit('interaction', event);
    },
    async _ensurePlayer() {

      if (this._playerPromise) return this._playerPromise;
      if (this.ytPlayer) return this.ytPlayer;
      this._playerPromise = (async () => {
      const YT = await loadYouTubeIframeAPI();
      return new Promise((resolve) => {
        this.ytPlayer = new YT.Player('rsm-yt-mount', {
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 0,
            controls: 0,
            fs: 0,             // removes fullscreen button
            disablekb: 1,      // disables keyboard shortcuts
            rel: 0, // restrict video suggestions to same channel (can't remove entirely)
            playsinline: 1,
            mute: store.isMuted ? 1 : 0,
          },
          events: {
            onReady: () => {
              this.playerReady = true;
              resolve(this.ytPlayer);
            },
            onStateChange: (e) => {
              // Don't auto-restart if an overlay (chatbot/inoculation) is open: without this
              // guard, a feed video ending right as the overlay opens can win a race against
              // the `paused` watcher's pauseVideo() call and keep looping audibly behind it.
              if (e.data === 0 && !this.paused) {
                this.ytPlayer.playVideo();
              }
            },
            onAutoplayBlocked: () => {
              store.isMuted = true;
              this.ytPlayer.mute();
              this.ytPlayer.playVideo();
            },
          },
        });
      });
      })();

      return this._playerPromise;
    },
    async onVideoActivated(post) {
      // Ignore duplicate activations for the video that's already showing (e.g. the mount-time
      // call and the first IntersectionObserver callback both fire for the first post).
      if (this.activeVideoId === post.id) return;
      this.post = post;
      const el = this.$refs.feedEl;
      if (!el) return;
      this.activeVideoId = this.post.id;

      // Always load the video into the shared player. (Previously this returned early when
      // videoLock === 'none', which skipped loading entirely and left the feed blank.)
      const player = await this._ensurePlayer();
      player.loadVideoById(this.post.youtubeId);

      // videoLock === 'none': play the video but never lock scrolling.
      if (this.videoLock === 'none') return;

      clearTimeout(this._scrollLockTimer);
      clearTimeout(this._scrollLockDelay);
      if (this._pendingLockHandler) {
        el.removeEventListener('scrollend', this._pendingLockHandler);
        this._pendingLockHandler = null;
      }

      // Apply the lock only after the CSS snap animation finishes, so overflow:hidden
      // doesn't cancel the snap mid-flight (which caused jitter).
      // scrollend fires when the snap settles; the fallback covers the first video (no preceding scroll).
      // Locking via overflow (not a covering overlay) keeps the action buttons tappable during the lock.
      let fired = false;
      const applyLock = () => {
        if (fired) return;
        fired = true;
        clearTimeout(this._scrollLockDelay);
        clearInterval(this._countdownInterval);
        this.lockActive = true;
        this.lockCountdown = 3;
        el.style.overflowY = 'hidden';
        this._countdownInterval = setInterval(() => {
          if (this.lockCountdown > 1) this.lockCountdown--;
        }, 1000);
        this._scrollLockTimer = setTimeout(() => {
          this.lockActive = false;
          if (this.$refs.feedEl) this.$refs.feedEl.style.overflowY = '';
          clearInterval(this._countdownInterval);
        }, 3000);
      };

      this._pendingLockHandler = applyLock;
      el.addEventListener('scrollend', applyLock, { once: true });
      this._scrollLockDelay = setTimeout(applyLock, 500);
    },
    toggleMute() {
      if (!this.ytPlayer) return;
      this.store.isMuted ? this.ytPlayer.mute() : this.ytPlayer.unMute();
    },
    togglePause(isPaused) {
      if (!this.ytPlayer || !this.activeVideoId) return;
      if (isPaused) this.ytPlayer.pauseVideo();
      else this.ytPlayer.playVideo();
    },
    _teardownPlayer() {
      // Mute AND pause the existing player rather than destroying it. Destroying/recreating
      // the iframe on every overlay open/close (2026-07-29 attempt) caused a production
      // incident: on a slow/unreliable connection, rebuilding the iframe from scratch on
      // resume could hang or fail entirely, leaving a permanently black, silent, frozen video
      // after the chatbot/inoculation closed. Muting first (before pausing) also directly
      // addresses the original audio-bleed concern without that reload risk.
      if (!this.ytPlayer) return;
      try {
        this.ytPlayer.mute();
        this.ytPlayer.pauseVideo();
      } catch (e) { /* player not ready yet */ }
    },
    _restorePlayer() {
      if (!this.ytPlayer) return;
      try {
        if (!this.store.isMuted) this.ytPlayer.unMute();
        this.ytPlayer.playVideo();
      } catch (e) { /* player not ready yet */ }
    },
    onVideoEvent(event) {
      this.$emit('video-event', event);
    },
    _onScroll() {
      clearTimeout(this._scrollTimer);
      this._scrollTimer = setTimeout(() => {
        const el = this.$refs.feedEl;
        if (!el) return;
        let top = el.scrollTop;
        const delta = top - this._lastScrollTop;
        if (delta > 10) this.$emit('scroll-change', 'down');
        else if (delta < -10) this.$emit('scroll-change', 'up');

        // scroll-snap-stop: always should stop the feed at every post, but a very fast flick
        // can still carry momentum scrolling past more than one snap point (iOS Safari
        // especially). If the settle position landed more than one post from where the swipe
        // started, snap back to exactly one post so a single swipe can't skip several videos.
        const postHeight = el.clientHeight;
        if (postHeight > 0) {
          const fromIndex = Math.round(this._lastScrollTop / postHeight);
          const toIndex = Math.round(top / postHeight);
          if (Math.abs(toIndex - fromIndex) > 1) {
            const clampedIndex = fromIndex + Math.sign(toIndex - fromIndex);
            top = clampedIndex * postHeight;
            el.scrollTo({ top, behavior: 'smooth' });
          }
        }

        this._lastScrollTop = top;

        // Infinite scroll: append more posts when near end
        if (el.scrollHeight - top - el.clientHeight < INFINITE_SCROLL_BUFFER_PX) {
          this._appendMorePosts();
        }
      }, 150);
    },
    _appendMorePosts() {
      if (this._appending) return;
      this._appending = true;

      const newBatch = this._sourcePosts.slice();
      if (this.shuffle) {
        for (let i = newBatch.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [newBatch[i], newBatch[j]] = [newBatch[j], newBatch[i]];
        }
      }
      const offset = this.posts.length;
      newBatch.forEach((p, i) => {
        this.posts.push(Object.assign({}, p, { id: p.id + '_' + offset + '_' + i }));
      });

      // Allow next append after a tick to prevent double-firing on one scroll event
      setTimeout(() => { this._appending = false; }, 500);
    },
  }
};
</script>
